import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Award, Save, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { TitleWithHelp } from '../components/ui/help-tooltip';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` } });

const TIPO_LABELS = {
  auto_menor_melhor: 'Automático',
  auto_frete: 'Automático',
  auto_condicao: 'Automático',
  auto_impostos: 'Automático',
  auto_garantia: 'Automático',
  manual: 'Manual',
};

const SupplierScoreConfig = () => {
  const [criterios, setCriterios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Supplier Score — NautilusOS";
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/configuracoes/supplier-score');
      setCriterios(res.data.criterios || []);
    } catch { toast.error('Erro ao carregar configuração'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/configuracoes/supplier-score', { criterios });
      toast.success('Configuração salva com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const updateCriterio = (idx, field, value) => {
    const updated = [...criterios];
    updated[idx] = { ...updated[idx], [field]: value };
    setCriterios(updated);
  };

  const qtdAtivos = criterios.filter(c => c.ativo).length;

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Carregando...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Cabeçalho padronizado */}
      <div className="flex items-center justify-between mb-6">
        <TitleWithHelp
          title="Supplier Score"
          description="Configure os critérios e pesos utilizados para calcular o score de cada fornecedor nas cotações. Os critérios ativos serão considerados automaticamente ao abrir o comparativo de respostas."
          connections={['Compras', 'Cotações']}
        >
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Award className="h-6 w-6 text-amber-500" />
            Supplier Score
          </h1>
        </TitleWithHelp>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchConfig} className="gap-2">
            <RotateCcw className="h-4 w-4" />Restaurar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2 bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salvar
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-500 mb-6">
        Defina quais critérios serão avaliados e o grau de importância de cada um.
        Quanto maior o peso, mais esse critério influencia o score final do fornecedor.
      </p>

      {/* Tabela de critérios */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Critérios de Avaliação</CardTitle>
            <Badge variant="outline" className="text-xs">
              {qtdAtivos} de {criterios.length} critérios ativos
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {/* Cabeçalho da tabela */}
            <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-slate-50 rounded-lg text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <div className="col-span-1">Ativo</div>
              <div className="col-span-4">Critério</div>
              <div className="col-span-2">Peso (0-10)</div>
              <div className="col-span-2">Preenchimento</div>
              <div className="col-span-3">Influência no Score</div>
            </div>

            {criterios.map((c, idx) => {
              const totalPesoAtivo = criterios.filter(cr => cr.ativo).reduce((s, cr) => s + (cr.peso || 0), 0);
              const impactPct = totalPesoAtivo > 0 && c.ativo ? ((c.peso / totalPesoAtivo) * 100).toFixed(1) : 0;
              return (
                <div key={c.id} className={`grid grid-cols-12 gap-3 px-3 py-3 rounded-lg items-center transition-colors ${c.ativo ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 opacity-50'}`}>
                  <div className="col-span-1">
                    <Switch checked={c.ativo} onCheckedChange={v => updateCriterio(idx, 'ativo', v)} />
                  </div>
                  <div className="col-span-4">
                    <p className="text-sm font-medium text-slate-800">{c.nome}</p>
                    {c.tipo === 'manual' && c.opcoes && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {c.opcoes.map(o => o.label).join(' | ')}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number" min={0} max={10}
                      value={c.peso}
                      onChange={e => updateCriterio(idx, 'peso', Math.min(10, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="h-8 w-20 text-center"
                      disabled={!c.ativo}
                    />
                  </div>
                  <div className="col-span-2">
                    <Badge variant={c.tipo === 'manual' ? 'secondary' : 'outline'} className="text-[10px]">
                      {TIPO_LABELS[c.tipo] || c.tipo}
                    </Badge>
                  </div>
                  <div className="col-span-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${impactPct}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 w-10 text-right">{impactPct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Como funciona */}
      <div className="mt-6 bg-slate-50 border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Como funciona o cálculo</h3>

        <p className="text-xs text-slate-600">
          Cada fornecedor recebe uma nota de 0 a 10 em cada critério. O score final é a média ponderada dessas notas,
          considerando apenas os critérios ativos e que foram preenchidos. O resultado vai de 0 a 100 pontos.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-lg border p-3">
            <h4 className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
              <Badge variant="outline" className="text-[9px]">Automático</Badge>
              Calculado pelo sistema
            </h4>
            <ul className="text-xs text-slate-600 space-y-1">
              <li><strong>Custo e Prazo de Entrega:</strong> O menor valor entre os fornecedores recebe nota 10, o maior recebe nota 0. Os demais são distribuídos proporcionalmente.</li>
              <li><strong>Frete:</strong> CIF (frete incluso) = nota 10 | FOB (frete por conta do comprador) = nota 5</li>
              <li><strong>Condição de Pagamento:</strong> À vista = 5 | 30 dias = 7 | 60 dias ou mais = 10</li>
              <li><strong>Impostos Inclusos:</strong> Sim = nota 10 | Não = nota 0</li>
              <li><strong>Prazo de Garantia:</strong> 12 meses ou mais = 10 | 6 a 12 meses = 7 | Menos de 6 meses = 3</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg border p-3">
            <h4 className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
              <Badge variant="secondary" className="text-[9px]">Manual</Badge>
              Preenchido pelo comprador
            </h4>
            <p className="text-xs text-slate-600 mb-1.5">
              Estes critérios são avaliados diretamente na tela de comparativo de cotações, onde o comprador seleciona a opção mais adequada para cada fornecedor:
            </p>
            <ul className="text-xs text-slate-600 space-y-1">
              <li><strong>Assistência Técnica:</strong> Completa/Local | Limitada | Inexistente</li>
              <li><strong>Capacidade Produtiva:</strong> Alta | Média | Baixa</li>
              <li><strong>Marca e Modelo:</strong> Aprovado | Aceitável | Não recomendado</li>
              <li><strong>Procedência:</strong> Nacional | Importado</li>
              <li><strong>Variação de Preço:</strong> Igual/menor | Pequena variação | Alta variação</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierScoreConfig;
